import { AnyAction, combineReducers, Reducer } from "redux";
import produce from "immer";

import { createInstanceMapReducer, EMPTY_ARRAY, EMPTY_OBJECT } from "@multiweb/reactUtils";
import { aggregationRowLogger } from "./logger";
import {
    AggregationRow,
    AggregationRows,
    ColumnKey,
    Columns,
    Filter,
    IAggregationFunctionSelectedAction,
    IAggregationRowMappedAction,
    IAggregationRowState,
    IClearAggregationRowAction,
    IColumnAddedAction,
    IColumnDefinitionsChangedAction,
    IColumnRemovedAction,
    IColumnWidthChangedAction,
    IFilterChangedAction,
    IInitAggregationRowAction,
    INumberOfFrozenColumnsChangedAction,
    IOrderOfColumnsChangedAction,
    ISetAggregationResultsAction,
    NullableClassName,
} from "./types";
import { createColumn } from "./utils";

const numberOfFrozenColumns: Reducer<number> = (state = 0, action) => {
    if (action instanceof IInitAggregationRowAction) {
        return action.payload.numberOfFrozenColumns;
    } else if (action instanceof INumberOfFrozenColumnsChangedAction) {
        return action.payload.numberOfFrozenColumns;
    } else if (action instanceof IColumnDefinitionsChangedAction) {
        return action.payload.numberOfFrozenColumns;
    } else {
        return state;
    }
};

const orderOfColumns: Reducer<ReadonlyArray<ColumnKey>> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof IInitAggregationRowAction) {
        return action.payload.orderOfColumns;
    } else if (action instanceof IOrderOfColumnsChangedAction) {
        return action.payload.orderOfColumns;
    } else if (action instanceof IColumnDefinitionsChangedAction) {
        return action.payload.orderOfColumns;
    } else if (action instanceof IColumnAddedAction) {
        return [...state, action.payload.column.key];
    } else if (action instanceof IColumnRemovedAction) {
        return state.filter((columnKey) => columnKey !== action.payload.columnKey);
    } else {
        return state;
    }
};

const columns: Reducer<Columns> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IInitAggregationRowAction) {
        return action.payload.columnDefinitions.reduce((result, columnDefinition) => ({
            ...result,
            [columnDefinition.key]: createColumn(columnDefinition),
        }), {} as Columns);
    } else if (action instanceof IColumnDefinitionsChangedAction) {
        return produce(state, (draft) => {
            action.payload.columnDefinitions.forEach((columnDefinition) => {
                const column = draft[columnDefinition.key];
                if (column && column.columnType === columnDefinition.columnType) {
                    const { aggregationFunction = null, aggregationResult = null } = column;
                    draft[columnDefinition.key] = { ...createColumn(columnDefinition), aggregationFunction, aggregationResult };
                } else {
                    draft[columnDefinition.key] = createColumn(columnDefinition);
                }
            });
        });
    } else if (action instanceof IColumnAddedAction) {
        return produce(state, (draft) => {
            const columnDefinition = action.payload.column;
            if (!draft[columnDefinition.key]) {
                draft[columnDefinition.key] = createColumn(columnDefinition);
            } else {
                aggregationRowLogger.warn("Cannot add column with key %s because it already exists.", columnDefinition.key);
            }
        });
    } else if (action instanceof IColumnRemovedAction) {
        return produce(state, (draft) => {
            delete draft[action.payload.columnKey];
        });
    } else if (action instanceof IAggregationFunctionSelectedAction) {
        return produce(state, (draft) => {
            const { columnKey, aggregationFunction } = action.payload;
            if (draft[columnKey]) {
                draft[columnKey].aggregationFunction = aggregationFunction ?? null;
                draft[columnKey].aggregationResult = null;
            }
        });
    } else if (action instanceof ISetAggregationResultsAction) {
        return produce(state, (draft) => {
            Object.entries(action.payload.results).forEach(([columnKey, aggregationResult]) => {
                if (draft[columnKey]) {
                    draft[columnKey].aggregationResult = aggregationResult;
                }
            });
        });
    } else if (action instanceof IColumnWidthChangedAction) {
        return produce(state, (draft) => {
            const { columnKey, width } = action.payload;
            if (draft[columnKey]) {
                draft[columnKey].width = width;
            }
        });
    } else {
        return state;
    }
};

const className: Reducer<NullableClassName> = (state = null, action) => {
    if (action instanceof IInitAggregationRowAction) {
        return action.payload.className;
    } else {
        return state;
    }
};

const filter: Reducer<Filter> = (state = { readVersioned: false, condition: "" }, action) => {
    if (action instanceof IInitAggregationRowAction) {
        return action.payload.filter;
    } else if (action instanceof IFilterChangedAction) {
        return action.payload.filter;
    } else {
        return state;
    }
};

const aggregationRow: Reducer<AggregationRow> = combineReducers({
    className,
    numberOfFrozenColumns,
    orderOfColumns,
    columns,
    filter,
});

const instanceKeyResolver = (action: AnyAction) => {
    return action instanceof IAggregationRowMappedAction ? action.meta.aggregationRowKey : undefined;
};

const instanceMapReducer = createInstanceMapReducer(aggregationRow, instanceKeyResolver);

const aggregationRows: Reducer<AggregationRows> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IClearAggregationRowAction) {
        return produce(state, (draft) => {
            delete draft[action.meta.aggregationRowKey];
        });
    } else {
        return instanceMapReducer(state, action);
    }
};

export const reducer: Reducer<IAggregationRowState> = combineReducers({
    aggregationRows,
});
